import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ESOganisationDropdown from "../components/dropdown/esOrganisationDropdown";
import SectionHeader from "../components/section/SectionHeader";
import {
  DFSI_CHANNELS_TABLE_HEADERS,
  DFSI_CONNECTIONS_FILTER_COLUMNS,
  EDIT_DFSI_CHANNEL_FORM_FIELDS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  errorAlert,
  makeApiRequest,
  successAlert,
  transformDFSIChannelResponseRow,
  transformDFSIChannelUpdateBody,
  transformDFSIChannelsRows,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import OrgContext from "../store/orgContext";
import InfoAlert from "../ui/InfoAlert";
import TableActionButton from "../ui/TableActionButton";
import AdvancedSearch from "../components/advancedSearch/AdvancedSearch";
import {
  deleteDFSIChannel,
  getDFSIChannelByRef,
  updateDFSIChannelByRef,
} from "../api";
import useHideSidePanel from "../hooks/useHideSidePanel";
import { INITIAL_DFSI_CHANNEL_EDIT_STATE } from "../constants/editState";
import EditDFSIChannelRow from "../components/dataTable/EditDFSIChannelRow";
import {
  BULK_UPLOAD_GENERAL_TRANSLATIONS,
  DELETE_DFSI_CHANNEL_TRANSLATIONS,
  READ_DFSI_CHANNEL_TRANSLATIONS,
} from "../translations/util";
import useAppTitle from "../hooks/useAppTitle";
import SearchContext from "../store/searchContext";
import BulkUpload from "../components/bulkUpload/BulkUpload";
import DFSIContext from "../store/dfsiContext";

interface ISearchData {
  "CONNECTION-ID"?: string | null;
}

const INITIAL_STATE = {
  "CONNECTION-ID": "",
};

const searchConfig = [
  {
    name: "CONNECTION-ID",
    label: "CONNECTION-ID",
    tooltipText: "CONNECTION-ID",
  },
];

const getAlertHeading = ({
  dfsiConnectionExists,
  channelsData,
}: {
  dfsiConnectionExists: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  channelsData: Array<any>;
}) => {
  if (!dfsiConnectionExists) {
    return READ_DFSI_CHANNEL_TRANSLATIONS.infoAlertHeading.dfsiConnectionExists;
  }

  if (!channelsData) {
    return READ_DFSI_CHANNEL_TRANSLATIONS.infoAlertHeading.channelsData;
  }

  return undefined;
};

const getAlertSubText = ({
  dfsiConnectionExists,
  channelsData,
}: {
  dfsiConnectionExists: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  channelsData: Array<any>;
}) => {
  if (!dfsiConnectionExists) {
    return READ_DFSI_CHANNEL_TRANSLATIONS.infoAlertSubText.dfsiConnectionExists;
  }

  if (!channelsData) {
    return READ_DFSI_CHANNEL_TRANSLATIONS.infoAlertSubText.channelsData;
  }

  return undefined;
};

const DFSIChannels = () => {
  const {
    token,
    scopes: { dfsi_channels: dfsiChannels },
    hexMode,
  } = useContext(AuthContext);
  const { orgs: orgList, selectedOrg, updateOrg } = useContext(OrgContext);
  const { selectedColumn, setSelectedColumn, setSearchText, setDisableSearch } =
    useContext(SearchContext);
  const { setDfsiConnectionData } = useContext(DFSIContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [channelsData, setChannelsData] = useState<any[]>([]);
  useAppTitle("DFSI Channels- IWF Config Admin Tool (ICAT) by AT&T");
  const {
    showSidePanel,
    showAdvancedSearch,
    handleToggleAdvanceSearchPanel,
    handleToggleSidePanel,
  } = useHideSidePanel();
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);

  const [searchData, setSearchData] = useState<ISearchData>(INITIAL_STATE);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
    sortByField?: string;
    sortDirection?: "desc" | "asc";
  }>({
    pageSize: 20,
    pageNum: 1,
    data: {},
    sortByField: undefined,
    sortDirection: undefined,
  });

  // State to check if Bulk Upload is Clicked
  const [isBulkUploadEnabled, setIsBulkUploadEnabled] = useState(false);

  const [dfsiConnectionExists, setDfsiConnectionExists] = useState(true);

  // Instructions to display in DFSI-Channel bulk upload pannel
  const DFSI_CHANNEL_NOTES = [
    BULK_UPLOAD_GENERAL_TRANSLATIONS.Notes.point1,
    BULK_UPLOAD_GENERAL_TRANSLATIONS.Notes.point2,
    BULK_UPLOAD_GENERAL_TRANSLATIONS.Notes.dfsiChannelNote1,
    BULK_UPLOAD_GENERAL_TRANSLATIONS.Notes.hexValueNote,
  ];

  // This function will fetch DFSI Connection's IDs and its respective version protocol
  const getDFSIConectionIds = async (orgId: string) => {
    try {
      const response = await makeApiRequest<{
        Rows: { "CONNECTION-ID": string; "DFSI-VERSION": number }[];
      }>({
        path: `/api/v1/app-config/dfsi-connections/ids/`,
        token,
        org: orgId,
      });
      setDfsiConnectionExists(response.Rows.length > 0);
      setDfsiConnectionData(response.Rows);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    }
  };

  const fetchDFSIChannels = async () => {
    setLoading(true);
    try {
      if (selectedOrg) {
        const { data, pageNum, pageSize, sortByField, sortDirection } =
          searchQuery;
        const queryParams = new URLSearchParams({
          page: `${pageNum}`,
          pageSize: `${pageSize}`,
          ...(sortByField && { sortByField }),
          ...(sortDirection && { sortDirection }),
          ...(data &&
            data["CONNECTION-ID"] && {
              "CONNECTION-ID": data["CONNECTION-ID"],
            }),
        });
        const response = await makeApiRequest<{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
        }>({
          path: `/api/v1/app-config/dfsi-channels?${queryParams}`,
          token,
          org: selectedOrg.id,
        });
        setNumberOfRows(response.NbrOfRows);
        setNumberOfPages(response.NbrOfPages);
        setChannelsData(response.Rows);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const deleteConnection = async (ref: string) => {
    if (selectedOrg && token) {
      try {
        await deleteDFSIChannel({
          token,
          ref,
          org: selectedOrg.id,
        });
        successAlert(DELETE_DFSI_CHANNEL_TRANSLATIONS.successAlertMessage);
        handleToggleSidePanel();
        setSelectedConnectionId(null);
        fetchDFSIChannels();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      }
    }
  };

  const sortTable = (col: string, direction: "asc" | "desc") => () => {
    setSearhQuery({
      ...searchQuery,
      sortByField: col,
      sortDirection: direction,
    });
  };

  useEffect(() => {
    setSelectedColumn(null);
    setSearchText("");
    setDisableSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchData(INITIAL_STATE);
    setSearhQuery({
      pageSize: 20,
      pageNum: 1,
      data: {},
      sortByField: undefined,
      sortDirection: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  useEffect(() => {
    fetchDFSIChannels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, searchQuery]);

  useEffect(() => {
    if (selectedOrg) {
      getDFSIConectionIds(selectedOrg.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  useEffect(() => {
    if (!showAdvancedSearch && Object.keys(searchQuery.data).length === 0) {
      setSearchData(INITIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvancedSearch]);

  return (
    <>
      <SectionHeader
        pageTitle={READ_DFSI_CHANNEL_TRANSLATIONS.header}
        pageDescription={READ_DFSI_CHANNEL_TRANSLATIONS.headerSubtext}
        sideComponent={
          <div className="dropdown-details">
            <ESOganisationDropdown
              org={selectedOrg || undefined}
              orgsList={orgList}
              setOrg={updateOrg}
            />
          </div>
        }
      />

      {selectedOrg ? (
        <>
          {showAdvancedSearch ? (
            <AdvancedSearch
              setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
              advancedSearchState={searchData}
              advancedSearchConfig={searchConfig}
              setAdvancedSearchState={setSearchData}
              onSubmit={(data) =>
                setSearhQuery({
                  ...searchQuery,
                  pageNum: 1,
                  pageSize: 20,
                  data,
                })
              }
              onCancel={() => {
                setSearchData(INITIAL_STATE);
                handleToggleAdvanceSearchPanel();
              }}
            />
          ) : null}

          {showSidePanel && isBulkUploadEnabled ? (
            <BulkUpload
              moduleName="DFSI Channel"
              pathName="/api/v1/app-config/dfsi-channels"
              csvTemplateFile={[
                "CONNECTION-ID,CHANNEL-ID,DFSI-VERSION,OUTBOUND-NETWORK-ACCESS-CODE,INBOUND-NETWORK-ACCESS-CODE,OTA-GROUP-ID,CHANNEL-MODE,CARRIER-SQUELCH,CTC-SQUELCH-SYSTEM,CDC-SQUELCH-SYSTEM",
              ]}
              closeBulkUploadSidePanel={() => {
                handleToggleSidePanel();
                setIsBulkUploadEnabled(false);
                fetchDFSIChannels();
              }}
              notes={DFSI_CHANNEL_NOTES}
            />
          ) : null}

          {showSidePanel &&
          selectedOrg &&
          selectedConnectionId &&
          !isBulkUploadEnabled ? (
            <EditDFSIChannelRow
              panelName={READ_DFSI_CHANNEL_TRANSLATIONS.editRow.panelName}
              initialState={INITIAL_DFSI_CHANNEL_EDIT_STATE}
              org={selectedOrg.id}
              esRef={selectedConnectionId}
              setShowEditPanel={handleToggleSidePanel}
              onDelete={() => deleteConnection(selectedConnectionId)}
              formFields={EDIT_DFSI_CHANNEL_FORM_FIELDS}
              header={READ_DFSI_CHANNEL_TRANSLATIONS.editRow.header}
              transformResponseRow={transformDFSIChannelResponseRow}
              transformUpdateBody={transformDFSIChannelUpdateBody}
              onUpdate={updateDFSIChannelByRef}
              loadDataByRef={getDFSIChannelByRef}
              hasDeletePermission={dfsiChannels && dfsiChannels.includes("d")}
              hasEditPermission={dfsiChannels && dfsiChannels.includes("u")}
              refreshTable={fetchDFSIChannels}
              deleteModalItemName="CHANNEL-ID"
            />
          ) : null}

          <DataTableWithControls
            loading={loading}
            showAdvancdSearch={false}
            filterColumns={DFSI_CONNECTIONS_FILTER_COLUMNS}
            selectedColumn={selectedColumn}
            setSelectedColumn={setSelectedColumn}
            onSearch={(term) =>
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: { [`${selectedColumn?.id}`]: term },
              })
            }
            setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
            showCreateButton={dfsiChannels && dfsiChannels.includes("c")}
            onCreateButtonClick={() => {
              navigate(`/configurationManagement/dfsiChannels/new`);
            }}
            rows={transformDFSIChannelsRows(channelsData, hexMode).map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (row: any) => ({
                ...row,
                action: (
                  <TableActionButton
                    onActionButtonClick={() => {
                      setSelectedConnectionId(row.id);
                      handleToggleSidePanel();
                    }}
                  />
                ),
              })
            )}
            numberOfAdvancedSearchFilters={searchConfig.reduce(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (acc, el: any) => {
                const { name } = el;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const obj: any = { ...searchData };
                return acc + (obj[name] && obj[name].length > 0 ? 1 : 0);
              },
              0
            )}
            clearAdvancedSearchFilters={() => {
              setSearchData(INITIAL_STATE);
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: {},
              });
            }}
            headers={DFSI_CHANNELS_TABLE_HEADERS}
            pageNum={searchQuery.pageNum}
            setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
            totalItems={numberOfRows}
            totalPages={numberOfPages}
            pageSize={searchQuery.pageSize}
            setPageSize={(pageSize) =>
              setSearhQuery({ ...searchQuery, pageSize })
            }
            sortByField={searchQuery.sortByField}
            sortDirection={searchQuery.sortDirection}
            handleSort={sortTable}
            infoAlertHeading={getAlertHeading({
              dfsiConnectionExists,
              channelsData,
            })}
            infoAlertSubtext={getAlertSubText({
              dfsiConnectionExists,
              channelsData,
            })}
            showBulkUploadButton={dfsiChannels && dfsiChannels.includes("c")}
            isDFSIdataExists={!dfsiConnectionExists}
            onBulkUploadButtonClick={() => {
              handleToggleSidePanel();
              setIsBulkUploadEnabled(true);
            }}
          />
        </>
      ) : (
        <InfoAlert
          heading={READ_DFSI_CHANNEL_TRANSLATIONS.infoAlert.heading}
          subText={READ_DFSI_CHANNEL_TRANSLATIONS.infoAlert.subText}
        />
      )}
    </>
  );
};

export default DFSIChannels;
